import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, AlertTriangle } from 'lucide-react';

export default function FourDsDisconnection() {
  const navigate = useNavigate();

  const fourDs = [
    {
      title: 'DIAGNOSIS',
      subtitle: 'Who is what?',
      meaning: 'Judging, labeling, criticizing',
      example: '"You are lazy and only care about yourself!"',
      form: 'You are __________.',
      color: 'from-red-900/30 to-orange-900/30',
      borderColor: 'border-red-700/30'
    },
    {
      title: 'DENIAL OF RESPONSIBILITY',
      subtitle: 'Who is to blame?',
      meaning: 'Denial of choice, blaming',
      example: '"I have to do what the boss says!"',
      form: 'You have to ________.', 
      color: 'from-orange-900/30 to-amber-900/30',
      borderColor: 'border-orange-700/30'
    },
    {
      title: 'DEMAND',
      subtitle: 'Who has power?',
      meaning: 'Implies threat of punishment',
      example: '"Get that proposal out by 5 pm! (or else you will be suspended)"',
      form: 'Do _______ or else!',
      color: 'from-amber-900/30 to-yellow-900/30',
      borderColor: 'border-amber-700/30'
    },
    {
      title: 'DESERVE',
      subtitle: 'Who deserves what?',
      meaning: 'Links behavior with punishment and/or reward',
      example: '"If you don\'t get that project done, don\'t bother coming in tomorrow!"',
      form: 'If _________, then _______.',
      color: 'from-yellow-900/30 to-red-900/30',
      borderColor: 'border-yellow-700/30'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">The Four D's of Disconnection</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 90</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-12">
            <AlertTriangle className="h-20 w-20 text-red-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">Each of the 4 D's is a tragic expression of an unmet need</h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              These patterns of communication block compassion and create disconnection. 
              Recognizing them helps us choose more connecting responses.
            </p>
          </div>

          {/* Four D's */}
          <div className="space-y-6 mb-12">
            {fourDs.map((d, idx) => (
              <div key={idx} className={`bg-gradient-to-br ${d.color} border ${d.borderColor} rounded-lg overflow-hidden`}>
                <div className="bg-slate-900/40 px-6 py-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-white">{d.title}</h3>
                      <p className="text-slate-300 italic">{d.subtitle}</p>
                    </div>
                    <div className="text-white/40 font-mono text-4xl">D{idx + 1}</div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-semibold text-white mb-2">Meaning:</p>
                      <p className="text-slate-200 mb-4">{d.meaning}</p>
                      <p className="text-sm font-semibold text-white mb-2">Form:</p>
                      <p className="text-slate-300 font-mono">{d.form}</p>
                    </div>
                    <div className="bg-slate-900/40 rounded p-4 border border-white/10">
                      <p className="text-sm font-semibold text-white mb-2">Example:</p>
                      <p className="text-slate-100">{d.example}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Exercise Section */}
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-6">Exercise</h3>
            <div className="space-y-4 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold text-white">1.</span> Write down at least one example of each of the 4 D's of Disconnection that you have heard recently.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-white">2.</span> Write down how you feel right now in response to each example. Link your feeling to your need.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-white">3.</span> Write down an empathic guess of what the person might have been feeling and needing when they said what you heard.
              </p>
              <div className="bg-blue-900/30 rounded p-4 mt-6">
                <p className="text-blue-200 font-semibold mb-2">Example:</p>
                <p className="text-blue-100 text-sm mb-1">1. "You are lazy and only care about yourself!"</p>
                <p className="text-blue-100 text-sm mb-1">2. I feel sad and frustrated because I have a need to be seen as a caring person.</p>
                <p className="text-blue-100 text-sm">3. Are you feeling frustrated because you value cooperation?</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
